<template>
  <section class="batch-modify freight-batch">
    <header class="batch-head">
      <div class="crumb">
        <button class="plain" :disabled="locked" @click="$emit('back')">‹ 返回</button
        ><span>商品工作台　›　批量修改　›　<strong>运费模板</strong></span>
      </div>
      <h1>批量修改运费模板</h1>
      <nav>
        <button
          v-for="tab in tabs"
          :key="tab.value"
          :class="{ active: mode === tab.value }"
          :disabled="locked"
          @click="switchMode(tab.value)"
        >
          {{ tab.label }}
        </button>
      </nav>
    </header>
    <main>
      <p v-if="optionsError" class="load-error">
        {{ optionsError }} <button class="plain" @click="loadOptions">重试加载</button>
      </p>
      <section class="panel">
        <div v-if="mode === 'products'" class="picked">
          <button :disabled="locked || templatesLoading" @click="openSelector">选择商品</button
          ><span
            >已选择 <b>{{ selected.length }}</b> 个商品</span
          >
        </div>
        <div v-else-if="mode === 'product_ids'" class="ids">
          <label>抖店商品 ID</label
          ><el-input
            v-model="idsText"
            :disabled="locked"
            type="textarea"
            :rows="3"
            placeholder="多个 ID 用逗号、空格或换行分隔，最多2000个"
          />
        </div>
        <div v-else class="filters" @keyup.enter="loadPreview(1)">
          <label v-for="field in shownFields" :key="field.key" class="field">
            <span>{{ field.key === 'freight_id' ? '原运费模板 ID' : field.label }}</span>
            <doudian-category-cascader
              v-if="field.key === 'category_id'"
              v-model="filters.category_id"
              :categories="categories"
              :disabled="locked"
            />
            <el-date-picker
              v-else-if="field.date"
              v-model="filters[field.key]"
              :disabled="locked"
              type="datetimerange"
              value-format="yyyy-MM-dd HH:mm:ss"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
            />
            <div v-else-if="field.matchKey" class="combined-control">
              <el-select v-model="filters[field.matchKey]" :disabled="locked"
                ><el-option v-for="o in field.matchOptions" :key="o.value" :value="o.value" :label="o.label"
              /></el-select>
              <div v-if="field.range" class="range">
                <input v-model="filters[field.key + '_min']" :disabled="locked" placeholder="最小值" /><span>~</span
                ><input v-model="filters[field.key + '_max']" :disabled="locked" placeholder="最大值" />
              </div>
              <input
                v-else
                v-model="filters[field.key]"
                :disabled="locked"
                :placeholder="field.placeholder || '请输入' + field.label"
              />
            </div>
            <div v-else-if="field.range" class="range">
              <input v-model="filters[field.key + '_min']" :disabled="locked" placeholder="最小值" /><span>~</span
              ><input v-model="filters[field.key + '_max']" :disabled="locked" placeholder="最大值" />
            </div>
            <el-select v-else-if="field.options" v-model="filters[field.key]" :disabled="locked" clearable
              ><el-option v-for="o in field.options" :key="String(o.value)" :value="o.value" :label="o.label"
            /></el-select>
            <input
              v-else
              v-model="filters[field.key]"
              :disabled="locked"
              :placeholder="field.placeholder || '请输入条件'"
            />
          </label>
        </div>
        <footer class="filter-actions">
          <div class="shop-row">
            <label for="freight-shop"><span class="required">*</span> 店铺</label>
            <el-select
              id="freight-shop"
              v-model="selectedShopId"
              clearable
              filterable
              :disabled="locked || optionsLoading"
              placeholder="请选择抖店（必选、单选）"
              aria-label="店铺（必选、单选）"
              @change="changeShop"
            >
              <el-option
                v-for="shop in shopOptions"
                :key="shop.mer_shop_id"
                :value="Number(shop.mer_shop_id)"
                :label="shop.shop_name"
              />
            </el-select>
            <small>本次仅修改当前店铺商品</small>
          </div>
          <div class="filter-buttons">
            <button :disabled="locked || loading || templatesLoading" @click="loadPreview(1)">查询</button
            ><button class="plain" :disabled="locked" @click="resetFilters">重置</button>
          </div>
        </footer>
        <p class="hint">按本地数据筛选，执行时读取抖店详情复核。</p>
      </section>
      <doudian-batch-common-options v-model="commonOptions" :disabled="locked || loading" />
      <section class="panel methods freight-content">
        <h3><i></i>修改内容</h3>
        <div class="template-row">
          <label for="freight-template"><span class="required">*</span> 模板选择</label>
          <el-select
            id="freight-template"
            v-model="templateId"
            filterable
            :filter-method="filterTemplates"
            :disabled="locked || !selectedShopId || templatesLoading || !templatesReady"
            :loading="templatesLoading"
            :placeholder="selectedShopId ? '请选择运费模板，可搜索名称或ID' : '请先选择店铺'"
            aria-label="运费模板"
            @visible-change="resetTemplateSearch"
          >
            <el-option
              v-for="item in visibleTemplates"
              :key="item.template_id"
              :value="item.template_id"
              :label="item.template_name + '（' + item.template_id + '）'"
            />
          </el-select>
          <a :href="newTemplateUrl" target="_blank" rel="noopener noreferrer">＋新建运费模板</a>
          <button class="plain" :disabled="locked || templatesLoading" @click="loadTemplates">
            {{ templatesLoading ? '刷新中…' : '刷新' }}
          </button>
        </div>
        <p v-if="templatesError" class="load-error" role="alert">{{ templatesError }}</p>
        <p v-else-if="templatesReady && !templates.length" class="hint">当前店铺暂无运费模板，请新建后刷新。</p>
        <p v-else-if="templatesReady" class="hint">已读取当前店铺全部 {{ templates.length }} 个运费模板。</p>
        <p class="hint">新建将在抖店打开新标签，请核对抖店当前登录店铺；新建后点击刷新。</p>
      </section>
      <section v-if="preview" class="panel preview">
        <header class="preview-summary">
          <span
            >候选 <b>{{ preview.total }}</b> 个，已排除 {{ preview.excluded_total }} 个，本次提交
            {{ preview.selected_total }} 个；本页预计命中 {{ counts.ready }}、预计跳过 {{ counts.skipped }}、待核验
            {{ counts.unknown }}、预计失败 {{ counts.failed }}</span
          ><button class="plain" :disabled="locked" @click="invalidate">收起预览</button>
        </header>
        <p class="hint">预览依据本地快照，实际命中和修改结果以执行时抖店详情复核及平台回执为准。</p>
        <p v-if="preview.missing_product_ids && preview.missing_product_ids.length" class="load-error">
          本地未匹配或不属于当前店铺的商品 ID：{{ preview.missing_product_ids.join('、') }}
        </p>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>商品信息</th>
                <th>原运费模板</th>
                <th>目标运费模板</th>
                <th>预览结果</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in preview.list" :key="item.id" :class="{ excluded: excluded.includes(Number(item.id)) }">
                <td>
                  <div class="preview-product">
                    <img v-if="item.main_image" :src="item.main_image" alt="商品主图" />
                    <div>
                      {{ item.product_title }}<small>{{ item.shop_name }}<br />{{ item.platform_product_id }}</small>
                    </div>
                  </div>
                </td>
                <td>
                  {{
                    item.old_freight_name || (item.old_freight_id == null ? '待核验' : '模板ID：' + item.old_freight_id)
                  }}
                </td>
                <td>
                  {{ item.target_freight_name }}<small>ID：{{ item.target_freight_id }}</small>
                </td>
                <td>
                  {{ resultLabels[item.preview_result] || '待核验' }}<small>{{ item.reason_text }}</small>
                </td>
                <td>
                  <el-checkbox
                    :value="excluded.includes(Number(item.id))"
                    :disabled="locked || loading"
                    @change="exclude(item.id, $event)"
                    >排除修改</el-checkbox
                  >
                </td>
              </tr>
              <tr v-if="!preview.list.length">
                <td colspan="5" class="empty">暂无符合条件的商品</td>
              </tr>
            </tbody>
          </table>
        </div>
        <footer class="preview-pagination">
          <el-pagination
            small
            layout="prev, pager, next"
            :disabled="locked || loading"
            :current-page="preview.page"
            :page-size="preview.limit"
            :total="preview.total"
            @current-change="loadPreview"
          />
        </footer>
      </section>
    </main>
    <footer class="actions">
      <button :disabled="locked || loading || templatesLoading" @click="loadPreview(1)">
        {{ loading ? '加载中…' : '修改内容预览' }}
      </button>
      <button v-if="createUncertain" class="start" :disabled="submitting" @click="submitFrozen">确认创建结果</button>
      <button v-else class="start" :disabled="locked || loading || templatesLoading" @click="createBatch">
        {{ submitting ? '创建中…' : '开始批量修改' }}
      </button>
      <label><input v-model="scheduled" type="checkbox" :disabled="locked" />定时执行</label>
      <el-date-picker
        v-if="scheduled"
        v-model="scheduledAt"
        :disabled="locked"
        type="datetime"
        value-format="yyyy-MM-dd HH:mm:ss"
        placeholder="选择北京时间"
      />
    </footer>
    <product-selector-dialog
      @page-size-change="changeSelectorPageSize"
      :visible="selectorVisible"
      :active-tab="selectorTab"
      :products="selectorProducts"
      :selected-ids="draftSelected.map(productId)"
      :loading="selectorLoading"
      :page="selectorPage"
      :page-size="selectorPageSize"
      :page-sizes="[20, 50, 100, 500, 1000]"
      :total="selectorTab === 'selected' ? draftSelected.length : selectorTotal"
      :total-pages="selectorPages"
      :jump-page="selectorJump"
      :page-fully-selected="selectorRows.length > 0 && selectorRows.every(isSelected)"
      :product-id="productId"
      :product-key="productId"
      :product-title="productTitle"
      :product-image="productImage"
      :product-price="productPrice"
      @tab-change="changeSelectorTab"
      @close="closeSelector"
      @toggle-product="toggleProduct"
      @toggle-page="togglePage"
      @page-change="changeSelectorPage"
      @jump-input="selectorJump = $event"
      @jump="changeSelectorPage(Number(selectorJump))"
      @confirm="confirmSelected"
    >
      <template #filters
        ><doudian-product-selector-filters
          :value="selectorFilters"
          :shop-id="selectedShopId"
          :shops="shopOptions"
          :categories="categories"
          lock-shop
          @query="loadSelector(1)"
      /></template>
    </product-selector-dialog>
  </section>
</template>

<script>
import {
  getDoudianFreightTemplates,
  previewDoudianFreightBatch,
  createDoudianFreightBatch,
  getDoudianCategories,
  getDoudianFreightShops,
  getProductSelectorProducts
} from '@/api/main/doudianBatchEdit'
import DoudianCategoryCascader from '@/components/DoudianCategoryCascader'
import ProductSelectorDialog from '@/components/ProductSelectorDialog/ProductSelectorDialog'
import DoudianBatchCommonOptions from '@/components/DoudianBatchCommonOptions'
import DoudianProductSelectorFilters from './DoudianProductSelectorFilters.vue'
import { fields, pairedFields, defaults, buildBatchProductFilter } from './batchModifyFilters'

const selectorDefaults = () => ({
  category_id: { level1: [], level2: [], level3: [] },
  platform_product_id: '',
  product_title: '',
  created_at: [],
  tab_status: ''
})
const supportedFields = fields.filter((field) => !['quality', 'has_exposure'].includes(field.key))

export default {
  name: 'BatchModifyFreight',
  components: {
    DoudianCategoryCascader,
    ProductSelectorDialog,
    DoudianBatchCommonOptions,
    DoudianProductSelectorFilters
  },
  data: () => ({
    tabs: [
      { value: 'range', label: '按范围修改' },
      { value: 'products', label: '按商品修改' },
      { value: 'product_ids', label: '按商品 ID 修改' }
    ],
    mode: 'range',
    filters: defaults(),
    selectedShopId: '',
    shopOptions: [],
    categories: [],
    optionsLoading: false,
    optionsError: '',
    templates: [],
    templateId: '',
    templateSearch: '',
    templatesReady: false,
    templatesLoading: false,
    templatesError: '',
    templateRequest: 0,
    newTemplateUrl:
      'https://fxg.jinritemai.com/ffa/morder/logistics/freight-list?spm=a26352.46514762.0.0.4d96AvMAAvMA9M',
    selected: [],
    excluded: [],
    idsText: '',
    commonOptions: { auto_generate_size_chart: false, shelf_action: 0 },
    scheduled: false,
    scheduledAt: '',
    preview: null,
    signature: '',
    loading: false,
    previewRequest: 0,
    requestId: '',
    confirming: false,
    submitting: false,
    createUncertain: false,
    frozenBody: null,
    selectorVisible: false,
    selectorTab: 'all',
    selectorLoading: false,
    selectorRows: [],
    selectorPageSize: 20,
    selectorPage: 1,
    selectorTotal: 0,
    selectorJump: 1,
    selectorFilters: selectorDefaults(),
    draftSelected: [],
    selectorRequest: 0,
    resultLabels: { ready: '预计命中', skipped: '预计跳过', unknown: '待核验', failed: '预计失败' }
  }),
  computed: {
    locked() {
      return this.confirming || this.submitting || this.createUncertain
    },
    shownFields() {
      return supportedFields
        .filter((f) => !Object.values(pairedFields).includes(f.key))
        .map((f) =>
          pairedFields[f.key]
            ? {
                ...f,
                matchKey: pairedFields[f.key],
                matchOptions: fields.find((x) => x.key === pairedFields[f.key]).options
              }
            : f
        )
    },
    visibleTemplates() {
      const q = this.templateSearch.trim().toLowerCase()
      return this.templates.filter(
        (item) => !q || item.template_name.toLowerCase().includes(q) || item.template_id.includes(q)
      )
    },
    previewCurrent() {
      return !!(this.preview && this.signature === JSON.stringify(this.payload(false)))
    },
    counts() {
      return this.preview?.page_match_counts || { ready: 0, skipped: 0, unknown: 0, failed: 0 }
    },
    selectorPages() {
      return Math.max(
        1,
        Math.ceil((this.selectorTab === 'selected' ? this.draftSelected.length : this.selectorTotal) / this.selectorPageSize)
      )
    },
    selectorProducts() {
      return this.selectorTab === 'selected'
        ? this.draftSelected.slice((this.selectorPage - 1) * this.selectorPageSize, this.selectorPage * this.selectorPageSize)
        : this.selectorRows
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.invalidate()
      }
    },
    commonOptions: {
      deep: true,
      handler() {
        this.invalidate()
      }
    },
    templateId() {
      this.invalidate()
    },
    idsText() {
      this.invalidate()
    },
    scheduled() {
      if (!this.scheduled) this.scheduledAt = ''
      this.invalidate()
    },
    scheduledAt() {
      this.invalidate()
    }
  },
  mounted() {
    this.newRequestId()
    this.loadOptions()
  },
  beforeDestroy() {
    this.templateRequest++
    this.previewRequest++
    this.selectorRequest++
  },
  methods: {
    changeSelectorPageSize(size) {
      this.selectorPageSize = Number(size)
      this.selectorPage = 1
      this.loadSelector(1)
    },
    dataOf(response) {
      if (!response || Number(response.code) !== 200) {
        const error = new Error(response?.message || '请求失败')
        error.businessFailure = true
        throw error
      }
      return response.data
    },
    message(error) {
      return error?.response?.data?.message || error?.message || '请求失败'
    },
    newRequestId() {
      this.requestId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0
        return (c === 'x' ? r : (r & 3) | 8).toString(16)
      })
    },
    invalidate() {
      this.previewRequest++
      this.preview = null
      this.signature = ''
      this.loading = false
      if (!this.locked) this.newRequestId()
    },
    async loadOptions() {
      this.optionsLoading = true
      this.optionsError = ''
      try {
        const data = this.dataOf(await getDoudianFreightShops())
        if (this._isDestroyed) return
        this.shopOptions = Array.isArray(data) ? data : data.list || data.data || []
        const categories = this.dataOf(await getDoudianCategories({ shop_type: 1 }))
        if (this._isDestroyed) return
        const map = (rows) =>
          rows.map((x) => ({
            ...x,
            category_id: x.id || x.category_id,
            category_name: x.name || x.category_name,
            children: map(x.children || [])
          }))
        this.categories = map(Array.isArray(categories) ? categories : categories.list || categories.data || [])
      } catch (error) {
        if (!this._isDestroyed) this.optionsError = this.message(error)
      } finally {
        if (!this._isDestroyed) this.optionsLoading = false
      }
    },
    changeShop() {
      this.templateRequest++
      this.templates = []
      this.templateId = ''
      this.templatesReady = false
      this.templatesError = ''
      this.templatesLoading = false
      this.selected = []
      this.excluded = []
      this.draftSelected = []
      this.selectorRows = []
      this.selectorTotal = 0
      this.selectorFilters = selectorDefaults()
      this.filters.category_id = { level1: [], level2: [], level3: [] }
      this.closeSelector()
      this.invalidate()
      if (this.selectedShopId) this.loadTemplates()
    },
    async loadTemplates() {
      if (!this.selectedShopId) return this.$message.warning('请先选择店铺')
      if (this.locked) return
      const request = ++this.templateRequest,
        shopId = this.selectedShopId,
        previousId = this.templateId
      this.invalidate()
      this.templatesReady = false
      this.templatesLoading = true
      this.templatesError = ''
      this.templateSearch = ''
      try {
        const data = this.dataOf(await getDoudianFreightTemplates({ shop_id: shopId }))
        if (request !== this.templateRequest || shopId !== this.selectedShopId || this._isDestroyed) return
        if (
          Number(data.shop_id) !== shopId ||
          data.complete !== true ||
          !Array.isArray(data.list) ||
          data.total !== data.list.length ||
          data.list.some((x) => typeof x.template_id !== 'string' || typeof x.template_name !== 'string')
        )
          throw new Error('运费模板响应不完整，请重试')
        this.templates = data.list
        this.templatesReady = true
        this.templateId = data.list.some((x) => x.template_id === previousId) ? previousId : ''
        if (previousId !== '' && this.templateId === '') this.$message.warning('该运费模板已失效，请重新选择')
      } catch (error) {
        if (request === this.templateRequest && !this._isDestroyed) this.templatesError = this.message(error)
      } finally {
        if (request === this.templateRequest && !this._isDestroyed) this.templatesLoading = false
      }
    },
    filterTemplates(query) {
      this.templateSearch = query
    },
    resetTemplateSearch() {
      this.templateSearch = ''
    },
    validateResources() {
      if (!this.selectedShopId) throw new Error('请先选择店铺')
      if (this.templatesLoading) throw new Error('运费模板加载中，请稍后重试')
      if (!this.templatesReady) throw new Error(this.templatesError || '请先刷新当前店铺运费模板')
      if (this.templateId === '' || !this.templates.some((x) => x.template_id === this.templateId))
        throw new Error('请选择当前店铺的运费模板')
    },
    payload(includeRequest = true) {
      let filter =
        this.mode === 'range'
          ? buildBatchProductFilter(this.filters, [this.selectedShopId], this.excluded, 'range')
          : {
              shop_ids: [Number(this.selectedShopId)],
              excluded_product_ids: [...this.excluded],
              platform_product_id:
                this.mode === 'products'
                  ? this.selected.map((x) => String(x.platform_product_id))
                  : [...new Set(this.idsText.split(/[,，\s]+/).filter(Boolean))]
            }
      delete filter.quality_min
      delete filter.quality_max
      delete filter.has_exposure
      const body = {
        selection_mode: this.mode,
        product_filter: filter,
        freight_rule: { template_id: this.templateId },
        common_options: { ...this.commonOptions }
      }
      if (includeRequest) {
        body.request_id = this.requestId
        if (this.scheduled) body.scheduled_at = this.scheduledAt
      }
      return body
    },
    validate() {
      this.validateResources()
      if (this.mode === 'products' && !this.selected.length) throw new Error('请选择商品')
      const ids = this.payload(false).product_filter.platform_product_id
      if (
        this.mode === 'product_ids' &&
        (!ids.length || ids.length > 2000 || ids.some((id) => !/^[1-9][0-9]{0,199}$/.test(id)))
      )
        throw new Error('请输入有效抖店商品 ID，最多2000个')
      if (this.selected.some((x) => Number(x.shop_id) !== this.selectedShopId))
        throw new Error('只能选择当前店铺的商品')
      if (
        this.scheduled &&
        (!this.scheduledAt ||
          !Number.isFinite(Date.parse(this.scheduledAt.replace(' ', 'T') + '+08:00')) ||
          Date.parse(this.scheduledAt.replace(' ', 'T') + '+08:00') <= Date.now())
      )
        throw new Error('定时时间必须为未来的北京时间')
    },
    switchMode(mode) {
      if (this.locked || this.mode === mode) return
      this.mode = mode
      this.selected = []
      this.excluded = []
      this.idsText = ''
      this.closeSelector()
      this.invalidate()
    },
    async loadPreview(page = 1) {
      if (this.locked) return
      try {
        this.validate()
      } catch (error) {
        return this.$message.warning(error.message)
      }
      const body = this.payload(false),
        signature = JSON.stringify(body),
        request = ++this.previewRequest
      this.loading = true
      try {
        const data = this.dataOf(await previewDoudianFreightBatch({ ...body, page, limit: 20 }))
        if (request !== this.previewRequest || signature !== JSON.stringify(this.payload(false)) || this._isDestroyed)
          return
        this.preview = data
        this.signature = signature
      } catch (error) {
        if (request === this.previewRequest && !this._isDestroyed) {
          this.invalidate()
          this.$message.error(this.message(error))
        }
      } finally {
        if (request === this.previewRequest && !this._isDestroyed) this.loading = false
      }
    },
    async createBatch() {
      try {
        this.validate()
        if (!this.previewCurrent) throw new Error('请先预览当前修改内容')
        if (!this.preview.selected_total || this.preview.total > 10000)
          throw new Error('候选商品需为1至10000件且不能全部排除')
      } catch (error) {
        return this.$message.warning(error.message)
      }
      const body = this.payload(),
        signature = JSON.stringify(body)
      this.confirming = true
      try {
        await this.$confirm(
          `将修改当前店铺 ${this.preview.selected_total} 个商品的运费模板，实际命中以执行时详情复核为准。确定开始吗？`,
          '确认修改'
        )
        this.validate()
        if (signature !== JSON.stringify(this.payload())) throw new Error('设置已变化，请重新预览')
        this.frozenBody = body
        await this.submitFrozen()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error(this.message(error))
      } finally {
        this.confirming = false
      }
    },
    async submitFrozen() {
      if (this.submitting || !this.frozenBody) return
      this.submitting = true
      try {
        this.dataOf(await createDoudianFreightBatch(this.frozenBody))
        this.createUncertain = false
        this.$message.success('任务已创建，请查看批量修改记录')
        this.$emit('created')
      } catch (error) {
        this.createUncertain = !error?.response && !error?.businessFailure
        this.$message.error(
          this.createUncertain ? '创建结果待核实，请点击确认创建结果；系统将复用同一请求' : this.message(error)
        )
      } finally {
        this.submitting = false
      }
    },
    exclude(id, checked) {
      const n = Number(id)
      this.excluded = checked ? [...new Set([...this.excluded, n])] : this.excluded.filter((x) => x !== n)
      this.invalidate()
    },
    resetFilters() {
      this.filters = defaults()
      this.selected = []
      this.excluded = []
      this.idsText = ''
      this.invalidate()
    },
    productId(item) {
      return Number(item.distribute_product_id || item.id)
    },
    productTitle(item) {
      return item.product_title || item.title || ''
    },
    productImage(item) {
      return item.main_image || item.img || ''
    },
    productPrice(item) {
      return item.product_price == null ? '价格待定' : '¥' + Number(item.product_price).toFixed(2)
    },
    isSelected(item) {
      return this.draftSelected.some((x) => this.productId(x) === this.productId(item))
    },
    openSelector() {
      try {
        this.validateResources()
      } catch (error) {
        return this.$message.warning(error.message)
      }
      this.draftSelected = [...this.selected]
      this.selectorTab = 'all'
      this.selectorVisible = true
      this.loadSelector(1)
    },
    closeSelector() {
      this.selectorRequest++
      this.selectorVisible = false
      this.selectorLoading = false
    },
    toggleProduct(item) {
      if (Number(item.shop_id) !== this.selectedShopId) return this.$message.warning('只能选择当前店铺的商品')
      this.draftSelected = this.isSelected(item)
        ? this.draftSelected.filter((x) => this.productId(x) !== this.productId(item))
        : [...this.draftSelected, item]
    },
    togglePage() {
      const all = this.selectorRows.every(this.isSelected)
      this.selectorRows.forEach((x) => {
        if (this.isSelected(x) === all) this.toggleProduct(x)
      })
    },
    changeSelectorTab(tab) {
      this.selectorRequest++
      this.selectorLoading = false
      this.selectorTab = tab
      this.changeSelectorPage(1)
    },
    changeSelectorPage(page) {
      const p = Math.max(1, Math.min(this.selectorPages, Number(page) || 1))
      if (this.selectorTab === 'all') this.loadSelector(p)
      else {
        this.selectorPage = p
        this.selectorJump = p
      }
    },
    confirmSelected() {
      if (this.draftSelected.some((x) => Number(x.shop_id) !== this.selectedShopId))
        return this.$message.warning('只能选择当前店铺的商品')
      this.selected = [...this.draftSelected]
      this.closeSelector()
      this.excluded = []
      this.invalidate()
    },
    async loadSelector(page) {
      try {
        this.validateResources()
      } catch (error) {
        return this.$message.warning(error.message)
      }
      const request = ++this.selectorRequest,
        shopId = this.selectedShopId
      this.selectorLoading = true
      try {
        const data = this.dataOf(
          await getProductSelectorProducts({ ...this.selectorFilters, page, page_size: this.selectorPageSize, shop_ids: [shopId] })
        )
        if (request !== this.selectorRequest || shopId !== this.selectedShopId || this._isDestroyed) return
        this.selectorRows = data.list || []
        this.selectorPage = page
        this.selectorJump = page
        this.selectorTotal = Number(data.pagination?.total || data.total || 0)
      } catch (error) {
        if (request === this.selectorRequest && !this._isDestroyed) this.$message.error(this.message(error))
      } finally {
        if (request === this.selectorRequest && !this._isDestroyed) this.selectorLoading = false
      }
    }
  }
}
</script>

<style scoped src="./batch-modify.css"></style>
<style scoped>
.shop-row,
.template-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 14px 0;
}
.filter-actions > .shop-row {
  flex: 1 1 520px;
  margin-right: auto;
  padding: 0;
}
.shop-row .el-select {
  flex: 0 1 220px;
  min-width: 0;
  max-width: 100%;
}
.shop-row label,
.template-row label {
  min-width: 78px;
  font-size: 12px;
}
.template-row .el-select {
  width: 340px;
  max-width: 100%;
}
.required,
.load-error {
  color: #c8443c;
}
.hint,
.shop-row small {
  font-size: 12px;
  color: #909399;
}
.freight-content {
  padding: 16px;
}
.template-row a {
  color: #409eff;
  font-size: 12px;
  text-decoration: none;
}
.table-wrap {
  overflow-x: auto;
}
.preview table {
  min-width: 700px;
}
.preview td small {
  display: block;
  color: #909399;
  margin-top: 5px;
  overflow-wrap: anywhere;
}
.preview-product {
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 180px;
}
.preview-product img {
  width: 44px;
  height: 44px;
  object-fit: cover;
}
.preview-product div {
  max-width: 260px;
  overflow-wrap: anywhere;
}
.excluded {
  opacity: 0.5;
}
.actions {
  flex-wrap: wrap;
}
@media (max-width: 768px) {
  .filter-actions > .shop-row {
    flex: 0 1 auto;
  }
}
@media (max-width: 600px) {
  .template-row .el-select,
  .shop-row .el-select {
    width: 100%;
  }
  .shop-row label,
  .template-row label {
    width: 100%;
  }
}
</style>
